import React from 'react';
import { Manga, TierGrade, TIER_CONFIGS, TIER_ORDER } from '../types/manga';
import { MangaCard } from './MangaCard';
import { formatImageUrl } from '../utils/helpers';

/** Section heading for one tier: tinted grade badge, its name, the count and a hairline */
export const TierHeading: React.FC<{ grade: TierGrade; count: number }> = ({ grade, count }) => {
  const config = TIER_CONFIGS[grade];
  return (
    <div className="flex items-center gap-3 mb-3.5 sm:mb-4">
      <span
        className={`inline-flex items-center justify-center min-w-[2.25rem] h-8 px-2 rounded-lg ring-1 ring-inset text-sm font-bold tracking-tight ${config.badge}`}
      >
        {grade}
      </span>
      <div className="flex items-baseline gap-2 min-w-0">
        <h2 className="text-sm sm:text-[15px] font-semibold text-zinc-100 truncate">{config.subtitle}</h2>
        <span className="text-xs text-zinc-500 tabular-nums whitespace-nowrap">{count} เรื่อง</span>
      </div>
      <div className="flex-1 h-px bg-white/[0.06]" />
    </div>
  );
};

interface TierBoardProps {
  mangaList: Manga[];
  searchQuery: string;
  isSortMode: boolean;
  onQuickPlus: (id: string) => void;
  onQuickMinus: (id: string) => void;
  onEdit: (manga: Manga) => void;
  onOpenSourceDetails: (manga: Manga) => void;
  onReorder: (draggedId: string, targetId: string) => void;
  onMoveUp?: (id: string) => void;
  onMoveDown?: (id: string) => void;
  onMoveToTier?: (mangaId: string, targetGrade: TierGrade) => void;
  onStartSortMode?: () => void;
}

export const TierBoard: React.FC<TierBoardProps> = ({
  mangaList,
  searchQuery,
  isSortMode,
  onQuickPlus,
  onQuickMinus,
  onEdit,
  onOpenSourceDetails,
  onReorder,
  onMoveUp,
  onMoveDown,
  onMoveToTier,
  onStartSortMode,
}) => {
  const [draggedMangaId, setDraggedMangaId] = React.useState<string | null>(null);
  const [hoverTargetId, setHoverTargetId] = React.useState<string | null>(null);
  const [hoverTierGrade, setHoverTierGrade] = React.useState<TierGrade | null>(null);
  // Only whether a touch drag is running lives in state; the finger position goes straight
  // to the preview's style so moving doesn't re-render the whole board
  const [isTouchDragging, setIsTouchDragging] = React.useState(false);

  const draggedMangaIdRef = React.useRef<string | null>(null);
  const hoverTargetIdRef = React.useRef<string | null>(null);
  const hoverTierGradeRef = React.useRef<TierGrade | null>(null);
  const previewRef = React.useRef<HTMLDivElement | null>(null);
  const fingerRef = React.useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const frameRef = React.useRef<number | null>(null);

  draggedMangaIdRef.current = draggedMangaId;
  hoverTargetIdRef.current = hoverTargetId;
  hoverTierGradeRef.current = hoverTierGrade;

  // Latest props for the stable handlers below
  const propsRef = React.useRef({ isSortMode, onReorder, onMoveToTier });
  propsRef.current = { isSortMode, onReorder, onMoveToTier };

  const query = searchQuery.toLowerCase().trim();
  const filteredList = query
    ? mangaList.filter((m) => m.title.toLowerCase().includes(query))
    : mangaList;

  const tiers = TIER_ORDER;

  const setHover = (targetId: string | null, grade: TierGrade | null) => {
    if (hoverTargetIdRef.current !== targetId) {
      hoverTargetIdRef.current = targetId;
      setHoverTargetId(targetId);
    }
    if (hoverTierGradeRef.current !== grade) {
      hoverTierGradeRef.current = grade;
      setHoverTierGrade(grade);
    }
  };

  // Desktop HTML5 Drag Handlers (stable so memoized cards don't re-render)
  const handleDragStart = React.useCallback((e: React.DragEvent, id: string) => {
    if (!propsRef.current.isSortMode) return;
    setDraggedMangaId(id);
    draggedMangaIdRef.current = id;
    e.dataTransfer.setData('text/plain', id);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = React.useCallback((e: React.DragEvent, targetId?: string) => {
    if (!propsRef.current.isSortMode) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (targetId && targetId !== draggedMangaIdRef.current) {
      setHover(targetId, null);
    }
  }, []);

  const handleDrop = React.useCallback((e: React.DragEvent, targetId: string) => {
    if (!propsRef.current.isSortMode) return;
    e.preventDefault();
    const sourceId = draggedMangaIdRef.current || e.dataTransfer.getData('text/plain');
    if (sourceId && sourceId !== targetId) {
      propsRef.current.onReorder(sourceId, targetId);
    }
    setDraggedMangaId(null);
    setHover(null, null);
  }, []);

  const handleTierDragOver = (e: React.DragEvent, grade: TierGrade) => {
    if (!isSortMode) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setHover(hoverTargetIdRef.current, grade);
  };

  const handleTierDrop = (e: React.DragEvent, grade: TierGrade) => {
    if (!isSortMode) return;
    e.preventDefault();
    const sourceId = draggedMangaIdRef.current || e.dataTransfer.getData('text/plain');
    if (sourceId && onMoveToTier) {
      onMoveToTier(sourceId, grade);
    }
    setDraggedMangaId(null);
    setHover(null, null);
  };

  const handleDragEnd = React.useCallback(() => {
    setDraggedMangaId(null);
    setHover(null, null);
  }, []);

  const positionPreview = () => {
    const el = previewRef.current;
    if (!el) return;
    const { x, y } = fingerRef.current;
    el.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -65%) scale(1.08)`;
  };

  // Mobile Touch Drag Coordinator (iOS Home Screen Experience)
  // One finger holds the card; any other finger on the screen scrolls the page, and holding the
  // card near the top/bottom edge scrolls too, so long tiers can be crossed mid-drag.
  const handleTouchStartDrag = React.useCallback((e: React.TouchEvent, manga: Manga) => {
    const touch = e.touches[0];
    if (!touch || draggedMangaIdRef.current) return; // a second finger must not start another drag

    const dragTouchId = touch.identifier;
    const otherFingers = new Map<number, number>(); // identifier -> last clientY
    let pendingScroll = 0;
    let fingerMoved = true;

    setDraggedMangaId(manga.id);
    draggedMangaIdRef.current = manga.id;
    fingerRef.current = { x: touch.clientX, y: touch.clientY };
    setIsTouchDragging(true);

    // Edge zones sit just inside the sticky header and the phone's bottom nav
    const headerBottom = document.querySelector('header')?.getBoundingClientRect().bottom ?? 0;
    const navTop = document.querySelector('nav.fixed')?.getBoundingClientRect().top ?? window.innerHeight;
    const EDGE = 70;

    const hitTest = () => {
      const { x, y } = fingerRef.current;
      const elem = document.elementFromPoint(x, y);
      const tId = elem?.closest('[data-manga-id]')?.getAttribute('data-manga-id');
      if (tId && tId !== draggedMangaIdRef.current) {
        setHover(tId, null);
        return;
      }
      const tGrade = elem?.closest('[data-tier-grade]')?.getAttribute('data-tier-grade') as TierGrade | null;
      setHover(null, tGrade || null);
    };

    // One loop per frame for the whole drag: move the preview, scroll, then hit-test once
    const onFrame = () => {
      frameRef.current = requestAnimationFrame(onFrame);
      const { y } = fingerRef.current;

      let scrollBy = pendingScroll;
      pendingScroll = 0;
      if (y < headerBottom + EDGE) {
        scrollBy -= Math.ceil(((headerBottom + EDGE - y) / EDGE) * 18);
      } else if (y > navTop - EDGE) {
        scrollBy += Math.ceil(((y - (navTop - EDGE)) / EDGE) * 18);
      }
      const before = window.scrollY;
      if (scrollBy !== 0) window.scrollBy(0, scrollBy);
      const scrolled = window.scrollY !== before;

      if (fingerMoved) positionPreview();
      if (fingerMoved || scrolled) hitTest();
      fingerMoved = false;
    };
    frameRef.current = requestAnimationFrame(onFrame);

    const handleWindowTouchStart = (ev: TouchEvent) => {
      for (const t of Array.from(ev.changedTouches)) {
        if (t.identifier !== dragTouchId) otherFingers.set(t.identifier, t.clientY);
      }
    };

    const handleWindowTouchMove = (ev: TouchEvent) => {
      // Prevent native scrolling/zooming; scrolling is done by hand above
      if (ev.cancelable) {
        ev.preventDefault();
      }

      for (const t of Array.from(ev.touches)) {
        if (t.identifier === dragTouchId) {
          fingerRef.current = { x: t.clientX, y: t.clientY };
          fingerMoved = true;
        } else {
          const last = otherFingers.get(t.identifier);
          // Content follows the finger: swipe up -> page moves up (scroll down)
          if (last !== undefined) pendingScroll += last - t.clientY;
          otherFingers.set(t.identifier, t.clientY);
        }
      }
    };

    const handleWindowTouchEnd = (ev: TouchEvent) => {
      const ended = Array.from(ev.changedTouches);
      for (const t of ended) otherFingers.delete(t.identifier);
      // Lifting the scrolling finger keeps the drag going
      if (!ended.some((t) => t.identifier === dragTouchId)) return;

      if (frameRef.current != null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }

      const activeId = draggedMangaIdRef.current;
      const targetId = hoverTargetIdRef.current;
      const targetGrade = hoverTierGradeRef.current;
      const { onReorder: reorder, onMoveToTier: moveToTier } = propsRef.current;

      if (activeId && targetId && targetId !== activeId) {
        reorder(activeId, targetId);
        if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
          navigator.vibrate(20);
        }
      } else if (activeId && targetGrade && moveToTier) {
        moveToTier(activeId, targetGrade);
        if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
          navigator.vibrate(20);
        }
      }

      setDraggedMangaId(null);
      draggedMangaIdRef.current = null;
      setHover(null, null);
      setIsTouchDragging(false);

      window.removeEventListener('touchstart', handleWindowTouchStart);
      window.removeEventListener('touchmove', handleWindowTouchMove);
      window.removeEventListener('touchend', handleWindowTouchEnd);
      window.removeEventListener('touchcancel', handleWindowTouchEnd);
    };

    window.addEventListener('touchstart', handleWindowTouchStart, { passive: true });
    window.addEventListener('touchmove', handleWindowTouchMove, { passive: false });
    window.addEventListener('touchend', handleWindowTouchEnd);
    window.addEventListener('touchcancel', handleWindowTouchEnd);
  }, []);

  // App re-renders often (e.g. background chapter checks); hand cards stable callbacks so the
  // memoized cards only re-render when their own manga changes
  const appCallbacks = React.useRef({ onQuickPlus, onQuickMinus, onEdit, onOpenSourceDetails, onMoveUp, onMoveDown, onStartSortMode });
  appCallbacks.current = { onQuickPlus, onQuickMinus, onEdit, onOpenSourceDetails, onMoveUp, onMoveDown, onStartSortMode };
  const stable = React.useMemo(
    () => ({
      onQuickPlus: (id: string) => appCallbacks.current.onQuickPlus(id),
      onQuickMinus: (id: string) => appCallbacks.current.onQuickMinus(id),
      onEdit: (m: Manga) => appCallbacks.current.onEdit(m),
      onOpenSourceDetails: (m: Manga) => appCallbacks.current.onOpenSourceDetails(m),
      onMoveUp: (id: string) => appCallbacks.current.onMoveUp?.(id),
      onMoveDown: (id: string) => appCallbacks.current.onMoveDown?.(id),
      onStartSortMode: () => appCallbacks.current.onStartSortMode?.(),
    }),
    []
  );

  const draggedManga = draggedMangaId
    ? mangaList.find((m) => m.id === draggedMangaId)
    : null;

  return (
    <main
      className={`w-full max-w-7xl mx-auto px-3 sm:px-6 pt-5 sm:pt-8 space-y-9 sm:space-y-12 relative ${
        draggedMangaId ? 'sort-dragging' : ''
      }`}
      style={{ paddingBottom: 'calc(7.5rem + env(safe-area-inset-bottom, 0px))' }}
    >
      {/* Floating Drag Preview on Mobile (iOS Icon Drag Effect) */}
      {isTouchDragging && draggedManga && (
        <div
          ref={(el) => {
            previewRef.current = el;
            positionPreview();
          }}
          className="fixed left-0 top-0 z-50 will-change-transform pointer-events-none w-28 sm:w-36 bg-surface rounded-xl overflow-hidden shadow-2xl shadow-black/80 ring-2 ring-amber-400 select-none"
        >
          <div className="relative aspect-[3/4] w-full bg-zinc-950 overflow-hidden">
            <img
              src={formatImageUrl(draggedManga.imageUrl)}
              alt={draggedManga.title}
              className="w-full h-full object-cover pointer-events-none"
            />
            <div className="absolute top-1.5 left-1.5 bg-amber-400 text-zinc-950 px-1.5 py-0.5 rounded-md text-[9px] font-semibold shadow">
              กำลังจัดเรียง
            </div>
          </div>
          <div className="px-2 py-1.5 text-[10px] font-medium text-zinc-100 truncate text-center">
            {draggedManga.title}
          </div>
        </div>
      )}

      {tiers.map((tierGrade) => {
        const tierManga = filteredList
          .filter((m) => m.grade === tierGrade)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const isTierHovered = hoverTierGrade === tierGrade;

        return (
          <section
            key={tierGrade}
            data-tier-grade={tierGrade}
            onDragOver={(e) => handleTierDragOver(e, tierGrade)}
            onDrop={(e) => {
              if (tierManga.length === 0) {
                handleTierDrop(e, tierGrade);
              }
            }}
            className={`rounded-2xl transition-[outline-color] duration-200 outline outline-1 outline-offset-[10px] ${
              isTierHovered ? 'outline-amber-400/70' : 'outline-transparent'
            }`}
          >
            <TierHeading grade={tierGrade} count={tierManga.length} />

            {/* Tier Cards Row - Exactly 3 columns on mobile, max 6 on PC */}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-x-2.5 gap-y-5 sm:gap-x-4 sm:gap-y-7 items-stretch">
              {tierManga.length === 0 ? (
                <div className="w-full col-span-3 sm:col-span-4 md:col-span-5 lg:col-span-6 py-10 rounded-xl border border-dashed border-white/[0.08] flex flex-col items-center justify-center text-zinc-500 text-xs sm:text-sm">
                  {query ? (
                    <p>ไม่พบมังงะที่ค้นหาในระดับนี้</p>
                  ) : (
                    <p>
                      ยังไม่มีมังงะในระดับนี้{' '}
                      {isSortMode && '(แตะหรือลากมังงะมาวางที่นี่)'}
                    </p>
                  )}
                </div>
              ) : (
                tierManga.map((manga, idx) => (
                  <MangaCard
                    key={manga.id}
                    manga={manga}
                    index={idx}
                    isSortMode={isSortMode}
                    isDragging={draggedMangaId === manga.id}
                    isDropTarget={hoverTargetId === manga.id}
                    onQuickPlus={stable.onQuickPlus}
                    onQuickMinus={stable.onQuickMinus}
                    onEdit={stable.onEdit}
                    onOpenSourceDetails={stable.onOpenSourceDetails}
                    onMoveUp={onMoveUp && stable.onMoveUp}
                    onMoveDown={onMoveDown && stable.onMoveDown}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onDragEnd={handleDragEnd}
                    onTouchStartDrag={handleTouchStartDrag}
                    onStartSortMode={onStartSortMode && stable.onStartSortMode}
                  />
                ))
              )}
            </div>
          </section>
        );
      })}
    </main>
  );
};
